import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import type { TColumn, TDataElement } from 'components/SummaryTable/SummaryTable.types'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter'
import { decamelize } from 'utils/decamelize'
import type { CurrentValuesDataInfo } from '../OverviewColumns'
import { blocksView, pointsView } from './SectionTable'
import type { CombinedDataItem, CurrentView } from './types'

export const formatLayer = (str: string) => (/[A-Z]/.test(str) ? decamelize(str) : capitalizeFirstLetter(str))

export const filterEmptyRows = (data: CombinedDataItem[], columns: TColumn[], currentView: string) => {
  if (!data || !columns) return []

  return data.filter((row) => {
    if (!row) return false

    if (pointsView.includes(currentView) && !blocksView.includes(currentView) && row.blockName) {
      return false
    }

    if (blocksView.includes(currentView) && !pointsView.includes(currentView) && row.pointName) {
      return false
    }

    return hasAnyData(row, columns)
  })
}

export const hasAnyData = (row: TDataElement, columns: TColumn[]) => {
  const dataColumnKeys = columns
    .map((col) => col.dataIndex)
    .filter((key) => !['pointName', 'blockName', 'propertyName', 'name', 'type', 'lastFetched'].includes(key))

  return dataColumnKeys.some((key) => {
    const value = row[key]

    // Add specific handling for trap catches
    if (key.includes('insectTrapCatches')) {
      return true
    }

    if (value === undefined) return false

    if (typeof value === 'number') return true

    if (typeof value === 'object' && value !== null) {
      const objectValues = Object.values(value)

      return objectValues.some((v) => {
        if (typeof v === 'number') return true

        return v !== undefined && v !== '' && v !== '-'
      })
    }

    if (typeof value === 'string') return value.trim() !== '' && value !== '-'

    return true
  })
}

export const filterColumnsByDataType = (columns: TColumn[], data: CombinedDataItem[]) => {
  // Check if there are any rows of each type
  const hasPoints = data.some((row) => row.type === 'point')
  const hasBlocks = data.some((row) => row.type === 'block')
  const hasProperties = data.some((row) => row.type === 'property')

  return columns.filter((col) => {
    // Keep the column if it's not a type identifier (pointName, blockName, propertyName)
    // or if we have data of that type
    if (col.key.includes('pointName')) return hasPoints

    if (col.key.includes('blockName')) return hasBlocks

    if (col.key.includes('propertyName')) return hasProperties

    return true
  })
}

export const getPropertyIdByPropertyName = (
  propertyName: string,
  properties: Record<number, TProperty>,
): number => {
  for (const [id, property] of Object.entries(properties)) {
    if (property.propertyName === propertyName) {
      return Number(id)
    }
  }

  return -1
}

export const getPropertyHemisphereByPropertyName = (
  propertyName: string,
  properties: Record<number, TProperty>,
): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const [_, property] of Object.entries(properties)) {
    if (property.propertyName === propertyName) {
      return property.isSouthernHemisphere
    }
  }

  return false
}

export const setGroupSelection = (
  keys: string[],
  selectedKeys: string[],
  timestamp: number,
  updatedSelection: Record<string, number | false>,
) => {
  // If any of the group keys are selected, select them all otherwise, deselect them all.
  const groupSelected = selectedKeys.some((key) => keys.includes(key))

  keys.forEach((key) => {
    updatedSelection[key] = groupSelected ? timestamp : false

    // Add to selectedKeys when selecting to keep everything in sync
    if (groupSelected && !selectedKeys.includes(key)) {
      selectedKeys.push(key)
    }
  })
}

export const getFavoriteDepths = (): Record<string, SharedTypes.TSoilSensorDepth[]> => {
  const properties = fieldAssetStore.getState().properties

  if (!properties) return {}

  const depthsByPoint: Record<string, SharedTypes.TSoilSensorDepth[]> = {}

  Object.values(properties).forEach((property) => {
    if (property?.points) {
      Object.values(property.points).forEach((point) => {
        if (point.lngLat) {
          depthsByPoint[point.lngLat] = point.configuration?.soilDefaultProbeDepths ?? []
        }
      })
    }
  })

  return depthsByPoint
}

export const pestSorter = (
  record: CurrentValuesDataInfo,
  prefix: string,
  pestType: string,
  currentView?: CurrentView | null,
) => {
  if (pestType === 'insectTrapCatches_') {
    const recordKey = Object.keys(record).find((key) => key.startsWith(pestType) && key.includes('insectId'))

    if (recordKey) {
      const insectId = recordKey.match(/insectId(\d+)/)?.[1]

      return record[`${prefix}_insectId${insectId}` as keyof CurrentValuesDataInfo] as number | null
    }
  } else if (currentView?.pest === 'BLOCK') {
    const recordKey = Object.keys(record).find(
      (key) => key.startsWith(prefix) && key.endsWith('_degreeDaysC'),
    )

    if (recordKey) return record[recordKey as keyof CurrentValuesDataInfo] as number | null
  } else if (currentView?.pest === 'CROP') {
    const dd = record.currentDD || record['7dayDD'] || record['14dayDD']

    if (dd && typeof dd === 'object' && dd.low !== null && dd.high !== null) {
      return dd.low
    }
  }

  return null
}

export const basicSort = (aVal: number | null, bVal: number | null) => {
  if (aVal === null && bVal === null) return 0

  if (aVal === null) return 1

  if (bVal === null) return -1

  return aVal - bVal
}

export const camelToKebab = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2') // lower to upper
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2') // acronym to capital+lower
    .replace(/([a-zA-Z])([0-9])/g, '$1-$2') // letter to number
    .toLowerCase()
}
